import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TrainerPermissionsModalProps {
  trainerName: string;
  initialCanRunOwnTrainings: boolean;
  initialIsEmployee: boolean;
  initialMaxNumberOfUsers?: number | null;
  onSave: (data: { canRunOwnTrainings: boolean; isEmployee: boolean; maxNumberOfUsers: number | null }) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export function TrainerPermissionsModal({
  trainerName,
  initialCanRunOwnTrainings = false,
  initialIsEmployee = false,
  initialMaxNumberOfUsers = null,
  onSave,
  onClose,
  isLoading = false,
}: TrainerPermissionsModalProps) {
  const { t } = useTranslation();
  const [canRunOwnTrainings, setCanRunOwnTrainings] = useState(initialCanRunOwnTrainings);
  const [isEmployee, setIsEmployee] = useState(initialIsEmployee);
  const [maxNumberOfUsers, setMaxNumberOfUsers] = useState<number | null>(initialMaxNumberOfUsers);
  const [hasMaxUsers, setHasMaxUsers] = useState(initialMaxNumberOfUsers !== null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      canRunOwnTrainings,
      isEmployee,
      maxNumberOfUsers: hasMaxUsers ? maxNumberOfUsers : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {t('trainers.editPermissions', 'Edytuj uprawnienia')}
            </h2>
            <p className="text-sm text-white/70">{trainerName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Is Employee */}
          <label className="flex items-center gap-3 p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/65 transition-colors">
            <input
              type="checkbox"
              checked={isEmployee}
              onChange={(e) => setIsEmployee(e.target.checked)}
              className="w-5 h-5 rounded border-border-strong text-brand-primary focus:ring-brand-primary focus:ring-offset-surface-card"
            />
            <div>
              <p className="font-medium text-white">
                {t('trainers.isEmployee', 'Pracownik etatowy')}
              </p>
              <p className="text-xs text-white/60">
                {t('trainers.isEmployeeDesc', 'Trener jest zatrudniony na etacie w firmie')}
              </p>
            </div>
          </label>

          {/* Can Run Own Trainings */}
          <label className="flex items-center gap-3 p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/65 transition-colors">
            <input
              type="checkbox"
              checked={canRunOwnTrainings}
              onChange={(e) => setCanRunOwnTrainings(e.target.checked)}
              className="w-5 h-5 rounded border-border-strong text-brand-primary focus:ring-brand-primary focus:ring-offset-surface-card"
            />
            <div>
              <p className="font-medium text-white">
                {t('trainers.canRunOwnTrainings', 'Własne treningi')}
              </p>
              <p className="text-xs text-white/60">
                {t('trainers.canRunOwnTrainingsDesc', 'Trener może tworzyć i prowadzić własne treningi')}
              </p>
            </div>
          </label>

          {/* Max Number of Users */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/65 transition-colors">
              <input
                type="checkbox"
                checked={hasMaxUsers}
                onChange={(e) => {
                  setHasMaxUsers(e.target.checked);
                  if (!e.target.checked) setMaxNumberOfUsers(null);
                  else setMaxNumberOfUsers(10);
                }}
                className="w-5 h-5 rounded border-border-strong text-brand-primary focus:ring-brand-primary focus:ring-offset-surface-card"
              />
              <div>
                <p className="font-medium text-white">
                  {t('trainers.limitUsers', 'Limit uczestników')}
                </p>
                <p className="text-xs text-white/60">
                  {t('trainers.limitUsersDesc', 'Ogranicz maksymalną liczbę uczestników na treningach')}
                </p>
              </div>
            </label>

            {hasMaxUsers && (
              <div className="pl-8">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={maxNumberOfUsers || ''}
                  onChange={(e) => setMaxNumberOfUsers(parseInt(e.target.value) || null)}
                  placeholder={t('trainers.maxUsersPlaceholder', 'Maksymalna liczba uczestników')}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:border-brand-primary"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-surface-card text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
            >
              {t('common.cancel', 'Anuluj')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common.saving', 'Zapisywanie...') : t('common.save', 'Zapisz')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
